import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { GET_CURRENT_USER_ENDPOINT } from 'reservations/base/constants/endpoints';
import { buildBentoboxApiEndpoint } from 'reservations/base/helpers/buildBentoboxApiEndpoint';
import { type RequestError, restClient } from 'utility/restClient';

const DinerAddress = z.object({
  fulfillment_method: z.string(),
  street_address_2: z.string(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string(),
  country: z.string(),
  fulfillment_contact_name: z.string(),
  fulfillment_contact_phone: z.string()
});
type DinerAddress = z.infer<typeof DinerAddress>;

const BillingAddress = z.object({
  street_address_1: z.string(),
  street_address_2: z.string(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string()
});
type BillingAddress = z.infer<typeof BillingAddress>;

const PaymentMethod = z.object({
  provider: z.string(),
  card_type: z.string(),
  card_last4: z.string(),
  card_expiration_month: z.number(),
  card_expiration_year: z.number()
});
type PaymentMethod = z.infer<typeof PaymentMethod>;

const UntransformedPaymentMethodListItem = z.object({
  id: z.number(),
  payment_method: PaymentMethod,
  billing_address: BillingAddress.nullable()
});
type UntransformedPaymentMethodListItem = z.infer<
  typeof UntransformedPaymentMethodListItem
>;

const PaymentMethodListItem = UntransformedPaymentMethodListItem.transform(
  paymentMethod => transformPaymentMethod(paymentMethod)
);

export type PaymentMethodListItem = z.infer<typeof PaymentMethodListItem>;

export const transformPaymentMethod = (
  paymentMethod: UntransformedPaymentMethodListItem
) => ({
  id: paymentMethod.id.toString(),
  cardType: paymentMethod.payment_method.card_type,
  cardLast4: paymentMethod.payment_method.card_last4,
  cardExpMonth: paymentMethod.payment_method.card_expiration_month,
  cardExpYear: paymentMethod.payment_method.card_expiration_year
});

export const DinerLoginResponse = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  addresses: z.array(DinerAddress),
  payment_methods: z.array(PaymentMethodListItem),
  formatted_phone_number: z.string(),
  phone_number: z.string()
});

export type DinerLoginResponse = z.infer<typeof DinerLoginResponse>;

export async function getCurrentUser() {
  const endpoint = buildBentoboxApiEndpoint(GET_CURRENT_USER_ENDPOINT, {
    is_booking_event: true
  });

  const response = await restClient.get(endpoint);

  try {
    return DinerLoginResponse.parse(response);
  } catch (err) {
    /* istanbul ignore next */
    throw err;
  }
}

export function useGetCurrentUserQuery({ dinerEmail }: Record<string, string>) {
  return useQuery<DinerLoginResponse, RequestError>({
    queryKey: ['currentUser', dinerEmail],
    queryFn: () => getCurrentUser(),
    retry: false,
    enabled: !!dinerEmail
  });
}
